import type { CreatorTagImportResult } from "@unbox/api";
import { FileX02, UploadCloud02 } from "@untitledui/icons";
import { useRef, useState } from "react";
import { useImportCreatorTagsCsv } from "@/api/creator-tags";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { success } from "@/providers/ToastProvider";

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

// Builds and downloads a CSV of the rows that failed to import: the original
// cells followed by a `reason` column.
const downloadErrorReport = (errors: CreatorTagImportResult["errors"]) => {
  const csv = errors.map((e) => [...e.row, e.reason].map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "creator-tags-import-errors.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ImportCsvDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { mutateAsync: importCsv, isPending } = useImportCreatorTagsCsv();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CreatorTagImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleImport = async () => {
    if (!file) return;
    try {
      const res = await importCsv(await file.text());
      setResult(res);
      success(
        `Imported ${res.assignments} tag assignments across ${res.creators} creators. ${res.tagsCreated} tags created.`,
      );
    } catch {
      // The global error handler surfaces the toast; keep the dialog open so the
      // user can correct and retry.
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>Import tags from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV of creator handles and tags. Unknown tags are created automatically.
          </DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-1 w-full py-6 rounded-lg border border-dashed border-primary transition-colors hover:border-brand"
        >
          <UploadCloud02 className="size-6 text-tertiary" />
          <p className="text-sm">
            <span className="font-semibold text-brand">Click to upload</span>
            <span className="text-tertiary"> a .csv file</span>
          </p>
          {file && <p className="text-xs text-secondary">{file.name}</p>}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            setResult(null);
            setFile(e.target.files?.[0] ?? null);
          }}
        />

        {result && result.errors.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-warning bg-warning-secondary p-3">
            <p className="text-sm text-secondary">
              {result.errors.length} row{result.errors.length === 1 ? "" : "s"} could not be matched to a creator.
            </p>
            <Button
              color="secondary"
              size="sm"
              iconLeading={FileX02}
              onClick={() => downloadErrorReport(result.errors)}
            >
              Download errors
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button color="secondary" onClick={() => handleOpenChange(false)}>
            {result ? "Done" : "Cancel"}
          </Button>
          <Button color="primary" isDisabled={!file} isLoading={isPending} onClick={handleImport}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
